import EMOJIS from "./emojis";
import Vue from "vue";
import axios from "axios";
import moment from "moment";
import { getConversation } from "@/composables/conversation";
import { ROLES } from "@/common/const";
import DOMPurify from "dompurify";
import { bus } from "@/main.js";

export function devMode() {
	return ["development"].includes(process.env.NODE_ENV);
}

export function prodMode() {
	return ["production"].includes(process.env.NODE_ENV);
}

// Infer a MIME type from a media URL's file extension. Used as a fallback when
// the stored attributes don't carry an explicit content type.
export const EXTENSION_TO_MIME = {
	// Images
	png: "image/png",
	jpg: "image/jpeg",
	jpeg: "image/jpeg",
	gif: "image/gif",
	bmp: "image/bmp",
	tif: "image/tiff",
	tiff: "image/tiff",
	// Audio
	au: "audio/basic",
	snd: "audio/basic",
	l24: "audio/L24",
	m4a: "audio/mp4",
	mp3: "audio/mpeg",
	ogg: "audio/ogg",
	oga: "audio/ogg",
	ra: "audio/vnd.rn-realaudio",
	wav: "audio/vnd.wave",
	ac3: "audio/ac3",
	amr: "audio/amr",
	"3ga": "audio/3gpp",
	// Video
	mpg: "video/mpeg",
	mpeg: "video/mpeg",
	mp4: "video/mp4",
	mov: "video/quicktime",
	webm: "video/webm",
	"3gp": "video/3gpp",
	"3g2": "video/3gpp2",
	h261: "video/H261",
	h263: "video/H263",
	h264: "video/H264",
	// Text
	vcf: "text/vcard",
	csv: "text/csv",
	rtf: "text/rtf",
	rtx: "text/richtext",
	ics: "text/calendar",
	// Application
	pdf: "application/pdf"
};

/**
 * Opens the browser print dialog directly for a file URL (e.g. a signed PDF
 * download URL), without requiring the user to download and open it first.
 * The file is fetched as a blob first so the iframe stays same-origin,
 * otherwise `contentWindow.print()` is blocked by cross-origin restrictions.
 * @param {string} url
 */
export async function printFileFromUrl(url) {
	const PRINT_DIALOG_DELAY_MS = 3000;

	const { data: fileBlob } = await axios.create().get(url, {
		responseType: "blob",
		withCredentials: false
	});

	const blobUrl = window.URL.createObjectURL(fileBlob);

	const iframe = document.createElement("iframe");

	iframe.style.position = "fixed";
	iframe.style.width = "0";
	iframe.style.height = "0";
	iframe.style.border = "0";

	document.body.appendChild(iframe);

	const cleanup = () => {
		window.removeEventListener("focus", cleanup);
		iframe.remove();
		window.URL.revokeObjectURL(blobUrl);
	};

	// Resolves once print() has been triggered, so callers can stop showing a
	// loading state right as the browser's print dialog is about to appear.
	// The delay gives the PDF viewer time to finish rendering after "load".
	try {
		await new Promise((resolve, reject) => {
			iframe.onload = () => {
				setTimeout(() => {
					try {
						iframe.contentWindow.focus();
						iframe.contentWindow.print();
						resolve();
					} catch (err) {
						reject(err);
					}
				}, PRINT_DIALOG_DELAY_MS);
			};
			iframe.onerror = () => reject(new Error("Failed to load file for printing"));
			iframe.src = blobUrl;
		});
	} catch (err) {
		cleanup();
		throw err;
	}

	window.addEventListener("focus", cleanup);
}

/**
 * Checks if the phone number is either an open phone or a twilio number in the database.
 * @param {*} phoneNumber - The phone number to verify.
 * @returns {boolean} - True if the phone number is an open phone or twilio, false otherwise.
 */
export async function isOpenPhoneOrTwilio(phoneNumber) {
	const { data: allOrganizations } = await axios.get("organizations");

	// Might be a little hard on the BE. Ideally have an endpoint with all twilio numbers.
	const phonesObj = await Promise.all(
		allOrganizations.map(async (org) => {
			const { data: data } = await axios.get(`organizations/${org.id}/phone-numbers`);

			return data;
		})
	);

	const phones = phonesObj.flat(Infinity).map((phone) => {
		return phone.phoneNumber.replace(/\D/g, "");
	});

	phones.push(...[process.env.VUE_APP_OPEN_PHONE_1, process.env.VUE_APP_OPEN_PHONE_2]);
	phoneNumber = phoneNumber.replace(/\D/g, "");
	return phones.includes(phoneNumber);
};

// True when the conversation is flagged as notified/unread.
// V2 exposes `unread`; V1 keeps it in (channelState.)attributes.hasNotification.
export function hasConversationNotification(conversation) {
	return !!(conversation?.unread ||
		conversation?.attributes?.hasNotification ||
		conversation?.channelState?.attributes?.hasNotification);
}

/**
 * Shared list-ordering comparator: pinned desc, then dateUpdated desc,
 * with document id desc as a V2 tiebreaker.
 * Missing conversations sort last.
 */
export function compareConversations(a, b) {
	if (!a && !b) return 0;
	if (!a) return 1;
	if (!b) return -1;

	const aPinned = !!(a.pinned || a.channelState?.attributes?.pinned);
	const bPinned = !!(b.pinned || b.channelState?.attributes?.pinned);

	if (aPinned !== bPinned) return aPinned ? -1 : 1;

	const dateDifference = (b.dateUpdated || 0) - (a.dateUpdated || 0);

	if (dateDifference || !a.id || !b.id) return dateDifference;
	if (a.id === b.id) return 0;

	return a.id > b.id ? -1 : 1;
}

export function getSortedPosition(array, conversation, pinnedCount = 0) {
	let low = pinnedCount;

	let high = array.length;

	while (low < high) {
		const mid = Math.floor((low + high) / 2);

		if (compareConversations(conversation, getConversation(array[mid])) < 0) {
			high = mid;
		} else {
			low = mid + 1;
		}
	}

	return low;
}

export function insertSorted(array, conversation, pinned, pinnedCount) {
	const isPinned = pinned ?? !!(conversation.pinned || conversation.channelState?.attributes?.pinned);
	const pos = getSortedPosition(array, {
		id: conversation.id,
		pinned: isPinned,
		dateUpdated: conversation.dateUpdated
	}, isPinned ? 0 : pinnedCount);

	array.splice(pos, 0, conversation.sid);
}

/**
 * Sorts an array of conversation sids in place. Decorate-sort-undecorate:
 * each sid is resolved once instead of on every comparison (getConversation
 * goes through the store on each call). The final splice keeps the write
 * reactive for Vue 2, which cannot observe direct index assignments.
 */
export function sortConversationSids(array) {
	const sorted = array
		.map((sid) => ({ sid, conversation: getConversation(sid) }))
		.sort((a, b) => compareConversations(a.conversation, b.conversation))
		.map((decorated) => decorated.sid);

	array.splice(0, array.length, ...sorted);
}

// Formats phone number to (XXX) XXX-XXXX
export function phoneNumberFormater(phoneNumber) {
	if (!phoneNumber) return "";
	const cleaned = `${phoneNumber.replace(/\D/g, "")}`;
	const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

	return match ? `(${match[2]}) ${match[3]}-${match[4]}` : phoneNumber;
};

// Get line type from a patient record
// export function getLineType(patient) {
// 	let result = null;

// 	try {
// 		const lookup = JSON.parse(patient.lookup);

// 		result = lookup?.lineTypeIntelligence?.type;
// 	} catch (err) {}

// 	try {
// 		const insight = JSON.parse(patient.insight);

// 		result = insight?.attributes?.connection || result;
// 	} catch (err) {}

// 	return result;
// }

export function formatToE164(phoneNumber) {
	const raw = String(phoneNumber ?? "").trim();
	const digits = raw.replace(/\D/g, "");

	if (!digits) return "";

	// 10 bare digits = NANP number missing its country code; a leading "+"
	// means the country code was typed explicitly.
	const needsCountryCode = digits.length === 10 && !raw.startsWith("+");

	return needsCountryCode ? `+1${digits}` : `+${digits}`;
}

export function initialsGenerator(obj) {
	if (!obj) {
		return undefined;
	}
	return fullName(obj).split(" ").filter(w => w).map(w => w[0]).join("").slice(0, 2);
};

export function initialLastNameGenerator(obj) {
	if (!obj) return "";

	const [fn, ln] = fullName(obj)?.split(" ") ?? [];
	const lastNameInitial = ln?.[0] ?? "";

	return `${fn ?? ""} ${lastNameInitial.toUpperCase()}`.trim();
}

export function fullName(obj) {
	if (!obj) return "";

	if (typeof obj === "string") {
		return obj.trim();
	}

	if (typeof obj.fullname === "string") {
		return obj.fullname.trim();
	}

	if (typeof obj.name === "string") {
		return obj.name;
	}

	return `${firstName(obj)} ${lastName(obj)}`.trim();
}

export function firstName(obj) {
	if (!obj) return "";

	if (typeof obj === "string") {
		return obj;
	}

	if (obj.firstname) {
		return `${obj.firstname ?? ""}`.trim();
	}

	if (typeof obj.first === "string") {
		return `${obj.first ?? ""}`.trim();
	}

	if (obj.name) {
		if (typeof obj.name === "string") {
			return obj.name.trim();
		}

		if (obj.name.first) {
			return `${obj.name.first ?? ""}`.trim();
		}
	}

	return "";
}

export function lastName(obj) {
	if (!obj) return "";

	if (typeof obj === "string") {
		return obj;
	}

	if (obj.lastname) {
		return `${obj.lastname ?? ""}`.trim();
	}

	if (typeof obj.last === "string") {
		return `${obj.last ?? ""}`.trim();
	}

	if (obj.name) {
		if (typeof obj.name === "string") {
			return obj.name.trim();
		}

		if (obj.name.last) {
			return `${obj.name.last ?? ""}`.trim();
		}
	}

	return "";
}

export async function fetchUsers() {
	const { data: users } = await axios.get("users");

	return users
		.map((user) => {
			return {
				identity: user.id,
				fullName: fullName(user.name),
				phoneNumber: user.settings ? user.settings.phoneNumber : "1111111111"
			};
		})
		.sort((userA, userB) => userA.fullName - userB.fullName);
};

export function convertTextToEmoji(text) {
	let textArr = text.split(" ");

	Object.entries(EMOJIS).map(([text, emoji]) => {
		textArr.map((newText, index) => {
			if (newText === text) {
				textArr[index] = emoji;
			}
		});
	});

	return textArr.join(" ");
};

export async function shortenURL(url) {
	const { data: tinyUrl } = await axios.post("link/create", { url });

	return tinyUrl;
};

export function validatePhoneForE164(phone) {
	if (!phone?.startsWith("+")) {
		return false;
	}

	phone = phone.replace(/\D/g, "");
	const regEx = /^[1-9]\d{10,14}$/;

	return regEx.test(phone);
};

export function birthdayFormater(birthday) {
	if (birthday) {
		return moment.utc(birthday).format("DD / MM / YYYY");
	} else {
		return "";
	}
}

export function birthdayPickerFormater(birthday) {
	if (birthday) {
		return moment.utc(birthday).format("YYYY-MM-DD");
	} else {
		return "";
	}
}

// Force download files
// downloadCallback() will receive both the href and the extension of the file to be downloaded
export async function download(url, downloadCallback) {
	let fileBlob;

	if (typeof url === "string" && url.startsWith("data:")) {
		fileBlob = await (await fetch(url)).blob();
	} else {
		({ data: fileBlob } = await axios.create().get(url, {
			responseType: "blob",
			withCredentials: false
		}));
	}

	const href = window.URL.createObjectURL(fileBlob);
	const extension = fileBlob.type.split("/")[1];

	try {
		await downloadCallback(href, extension);
	} catch (err) {
		throw err;
	} finally {
		window.URL.revokeObjectURL(href);
	}
};

export function currentLocale(locale) {
	// We only support "fr-CA" & "en-CA" for now
	const convertLocale = {
		"en-CA": "en-CA",
		"en-US": "en-CA",
		"en": "en-CA",
		"fr-CA": "fr-CA",
		"fr-FR": "fr-CA",
		"fr": "fr-CA"
	};

	return convertLocale[locale];
};

// Format time in sec to mm:ss or hh:mm:ss if needed
export function formatTime(time) {
	try {
		require("moment-duration-format");

		const duration = moment.duration(time, "seconds");
		const hours = Math.floor(time / 3600);

		if (hours > 0) {
			return duration.format("HH:mm:ss", { trim: false });
		} else {
			return duration.format("mm:ss", { trim: false });
		}
	} catch (error) {
		console.error(error);
	}
}

export function getBrowserLanguage() {
	const [userLanguage] = navigator.language.split("-");

	return currentLocale(userLanguage);
};

export async function getMicrophoneStatus() {
	try {
		let microphonePermissionGranted = false;

		// Request microphone access directly, using the preferred device if available
		try {
			let stream;

			try {
				const { getAudioDeviceManager } = await import("@/composables/useAudioDeviceManager");
				const audioDeviceManager = getAudioDeviceManager();

				stream = await audioDeviceManager.getUserMedia();
			} catch {
				// Fallback if AudioDeviceManager is not ready
				stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			}
			// If access is not granted, we open panel for granting access.

			microphonePermissionGranted = true;
			// Stop the audio tracks immediately since we only want to check permission
			stream.getTracks().forEach(track => track.stop());
		} catch (error) {
			// If we get an error, permission is denied
			microphonePermissionGranted = false;
		}
		return microphonePermissionGranted;
	} catch (err) {
		console.error(err);
	}
};

// Sanitize content and format it to include clickable links
export function formatContent(content) {
	const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/g;

	// Replace <...> tags with placeholders
	const placeholders = {};

	let index = 0;
	const contentWithPlaceholders = content.replace(/<([^>\s]+)>/g, (match) => {
		const key = `__CUSTOM_TAG_${index}__`;

		placeholders[key] = match; // Store the original tag
		index++;
		return key;
	});

	// Sanitize content with DOMPurify
	const sanitizedMessage = DOMPurify.sanitize(contentWithPlaceholders);

	// Restore original tags and escape < >
	let restoredMessage = Object.entries(placeholders).reduce(
		(acc, [key, value]) => acc.replace(new RegExp(key, "g"), value),
		sanitizedMessage
	);

	// Escape < and > to prevent Vue from treating them as real tags
	restoredMessage = restoredMessage.replace(/</g, "&lt;").replace(/>/g, "&gt;");

	// Convert URLs into clickable links
	const formattedMessage = restoredMessage.replace(urlRegex, (link) => {
		const url = link.startsWith("http") ? link : `https://${link}`;

		return `<a href='${url}' target='_blank' rel='noopener noreferrer'>${link}</a>`;
	});

	return formattedMessage;
}

// Open the link on a new tab for external links
export function addTargetBlankToLinks(content) {
	return content.replace(/<a([^>]+)>/gi, "<a target=\"_blank\" rel=\"noopener noreferrer\"$1>");
}

export function setViewVariable(obj, path, value) {
	let current = obj;

	for (let i = 0; i < path.length - 1; i++) {
		if (!current[path[i]]) {
			Vue.set(current, path[i], {});
		}
		current = current[path[i]];
	}
	Vue.set(current, path[path.length - 1], value);
}

export function streamToBuffer(stream) {
	return new Promise((resolve, reject) => {
		const buf = [];

		stream.on("data", (chunk) => buf.push(chunk));
		stream.on("end", () => resolve(Buffer.concat(buf)));
		stream.on("error", (err) => reject(err));
	});
}

export function hasAdminRolePermissions(userRole) {
	switch (userRole) {
		case ROLES.ADMIN:
		case ROLES.DEVOPS:
		case ROLES.SUPER_ADMIN:
			return true;
		default:
			return false;
	}
}

export function sortBreaks(week) {
	return week.map(day => {
		day.breaks = day.breaks.sort((a, b)	=> {
			const timeA = new Date(`01/01/2000 ${a.start}:00`).getTime();
			const timeB = new Date(`01/01/2000 ${b.start}:00`).getTime();

			return timeA - timeB;
		});
		return day;
	});
}

export function fileToBase64(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = (e) => resolve(e.target.result);
		reader.onerror = (error) => reject(error);
		reader.readAsDataURL(file);
	});
}

export function formatVacationPeriod(startDate, endDate, locale = moment.locale()) {
	// Apply locale (for month names, etc.)
	moment.locale(locale);

	const start = moment.utc(startDate).local();
	const end = moment.utc(endDate).local();

	// Helper: add English day suffixes (1st, 2nd, 3rd, etc.)
	const getDaySuffix = (day) => {
		if (locale.startsWith("fr")) return ""; // French doesn't use suffixes
		if (day >= 11 && day <= 13) return "th";
		switch (day % 10) {
			case 1: return "st";
			case 2: return "nd";
			case 3: return "rd";
			default: return "th";
		}
	};

	const startDay = start.date();
	const endDay = end.date();
	const startSuffix = getDaySuffix(startDay);
	const endSuffix = getDaySuffix(endDay);

	// Format rules differ slightly for French vs English
	const monthFormat = locale.startsWith("fr") ? "D MMMM" : "MMMM D";

	// Case 1: different years
	if (start.year() !== end.year()) {
		return `${start.format(monthFormat)}${startSuffix}, ${start.year()} - ${end.format(monthFormat)}${endSuffix}, ${end.year()}`;
	}

	// Case 2: different months (same year)
	if (start.month() !== end.month()) {
		return `${start.format(monthFormat)}${startSuffix} - ${end.format(monthFormat)}${endSuffix}, ${start.year()}`;
	}

	// Case 3: same month and same day
	if (startDay === endDay) {
		return `${start.format(monthFormat)}${startSuffix}, ${start.year()}`;
	}

	// Case 4: same month, different days
	if (locale.startsWith("fr")) {
		return `${start.format("D")} - ${end.format("D MMMM")}, ${start.year()}`;
	} else {
		return `${start.format("MMMM D")}${startSuffix} - ${end.format("D")}${endSuffix}, ${start.year()}`;
	}
}

export function waitForEvent(eventName, matchCondition, timeout = 5000) {
	return new Promise((resolve, reject) => {
		const handler = (eventData) => {
			if (matchCondition(eventData)) {
				bus.$off(eventName, handler);
				clearTimeout(timeoutId);
				resolve(eventData);
			}
		};

		bus.$on(eventName, handler);

		const timeoutId = setTimeout(() => {
			bus.$off(eventName, handler);
			reject(new Error(`Timed out waiting for ${eventName}`));
		}, timeout);
	});
}

/**
 * Extract recording ID (RE...) from a Twilio recording URL
 * @param {string} recordingUrl
 * @returns {string|null} Recording SID or null if not found
 */
export function extractRecordingIdFromUrl(recordingUrl) {
	if (!recordingUrl) return null;

	const match = recordingUrl.match(/Recordings\/(RE[0-9a-z]+)/i);

	if (match) {
		return match[1];
	}

	// Try to match just the RE... pattern
	const simpleMatch = recordingUrl.match(/RE[0-9a-z]{32}/i);

	if (simpleMatch) {
		return simpleMatch[0];
	}

	return null;
}

export function parseAndValidateDate(dateString, errorContext = "date") {
	// Already a Date object
	if (dateString instanceof Date) {
		if (isNaN(dateString.getTime())) {
			console.warn(`Invalid ${errorContext} - could not parse:`, dateString);
			return null;
		}
		return dateString;
	}

	// Handle Firestore timestamp objects
	if (dateString && typeof dateString === "object" && dateString.toDate) {
		const date = dateString.toDate();

		return date;
	}

	// Handle string dates - should be ISO format
	if (typeof dateString === "string") {
		const date = new Date(dateString);
		
		if (isNaN(date.getTime())) {
			console.warn(`Invalid ${errorContext} - could not parse string:`, dateString);
			return null;
		}

		return date;
	}

	// If not a string, Date, or Firestore timestamp, return null
	console.warn(`Invalid ${errorContext} type:`, dateString);
	return null;
}

export function formatDate(dateString) {
	const date = parseAndValidateDate(dateString, "date");

	if (!date) return "Invalid Date";
	
	// Convert to local date in YYYY-MM-DD format for the date input
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	
	const formattedDate = `${year}-${month}-${day}`;

	return formattedDate;
}

export function formatTimeHourMinute(dateString) {
	const date = parseAndValidateDate(dateString, "time");

	if (!date) return "Invalid Time";
	
	const hours = String(date.getHours()).padStart(2, "0");
	const minutes = String(date.getMinutes()).padStart(2, "0");
	
	const formattedTime = `${hours}:${minutes}`;

	return formattedTime;
}
